/**
 * TutorialShell — the scripted "server" of the in-game tutorial.
 *
 * The tutorial is NOT a real game: it needs no server connectivity and no
 * matchmaking. This class plays the role the authoritative backend normally
 * plays for the tutorial match — it owns the tutorial player's state (hand,
 * battlefield, gold, runes, ready flag) and answers the same client
 * intentions a real match would send (play, sell, reposition, buy, upgrade,
 * buy rune, ready), validating each one and emitting the resulting state
 * through a REAL GameStateService, so the real UI components
 * (HandUI / BattlefieldUI / MatchHUD / UpgradeChoiceUI / …) and the real
 * main.ts render glue react exactly as they do in a live match.
 *
 * Action gating: the tutorial walks the player through 11 tips in a fixed
 * order, and the user may only perform the CURRENT tip's action. The
 * TutorialService tells this shell which actions are allowed right now
 * (`setAllowedActions`); every intention is rejected (no state change) when
 * its action is not allowed — the tutorial's own "authoritative backend".
 *
 * Scripted rules:
 *  - 3 cards in hand at start: a Tank, a Bard and a Mage (from the shared
 *    testMinions pool — the same definitions the server draws from).
 *  - 0 gold at start (the tips grant gold exactly when an action needs it).
 *  - The preparation timer is FROZEN at TUTORIAL_TIMER_SECONDS (42) — the
 *    phase never locks and combat never starts: the user has infinite time.
 */

import { AppGamestate, GameStateService } from './GameStateService';
import {
  PROTOCOL_VERSION,
  GameStateUpdate,
  MatchStarted,
  ReadyStateUpdate,
  RoundStarted,
  UpgradeOptionsResponse,
  UpgradeOption,
  PlayerData,
  MinionInHandData,
  MinionOnBoardData,
} from '../../shared/messages/MessageContracts';
import { GameConfig } from '../../config/gameConfig';
import {
  RUNE_POWER_ID,
  getRuneById,
} from '../../shared/models/RuneDefinitions';
import { testMinions } from '../../shared/models/testMinions';

// ─── Types ─────────────────────────────────────────────────────────────────

/**
 * The player intentions the tutorial can gate. One per tip that requires an
 * action (the timer/gold/end tips need no action).
 */
export type TutorialAction =
  | 'play'
  | 'upgrade'
  | 'reposition'
  | 'sell'
  | 'buy'
  | 'rune'
  | 'ready'
  /**
   * The long-press tip: NOT an intention (there is no shell handler for it
   * — no request crosses the wire). Completion is detected by the
   * CardInspectService's long-press listener (TutorialService), and the
   * action simply keeps every other intention locked while the tip runs.
   */
  | 'longpress';

export interface TutorialShellEvent {
  type: 'stateChange';
  data: AppGamestate;
}

export type TutorialShellEventListener = (event: TutorialShellEvent) => void;

// ─── Constants ─────────────────────────────────────────────────────────────

/** The tutorial match id (it is never a real match — no server knows it). */
export const TUTORIAL_MATCH_ID = 'tutorial-match';

/** The frozen preparation-timer value shown during the tutorial (seconds). */
export const TUTORIAL_TIMER_SECONDS = 42;

/**
 * The tutorial's starting hand, by card ID (one Tank, one Bard, one Mage).
 * IDs come from the shared testMinions pool — the same pool the server's
 * card draw uses.
 */
export const TUTORIAL_STARTING_HAND: readonly string[] = ['tank', 'bard', 'mage'];

/**
 * The card a tutorial "Buy Card" produces (deterministic — the tutorial is
 * fully scripted, no RNG): a plain Warrior.
 */
export const TUTORIAL_BOUGHT_CARD_ID = 'warrior';

/** Upgrade branches the tutorial offers (the stat branches only — the
 *  upgrade tip teaches the mechanism, not the keyword catalogue). */
const TUTORIAL_UPGRADE_BRANCHES: ReadonlyArray<{ id: 'hp' | 'atk'; name: string; hpChange: number; attackChange: number }> = [
  { id: 'hp', name: 'Health Boost', hpChange: 5, attackChange: 0 },
  { id: 'atk', name: 'Attack Boost', hpChange: 0, attackChange: 5 },
];

// ─── Internal player shape ─────────────────────────────────────────────────

interface TutorialPlayer {
  id: string;
  life: number;
  gold: number;
  hand: MinionInHandData[];
  battlefield: MinionOnBoardData[];
  ready: boolean;
  /** The runes purchased so far (the tutorial script buys one: Power). */
  purchasedRunes: string[];
}

// ─── TutorialShell ─────────────────────────────────────────────────────────

export class TutorialShell {
  private readonly _playerId: string;
  private readonly _gameState: GameStateService;
  private readonly _player: TutorialPlayer;
  private readonly _listeners: TutorialShellEventListener[] = [];
  /** Actions the current tip allows (the tutorial's order enforcement). */
  private _allowedActions: Set<TutorialAction> = new Set();
  /** Sequence for generated instance IDs (bought cards). */
  private _instanceSeq = 0;
  private _started = false;

  constructor(playerId: string) {
    this._playerId = playerId;
    this._gameState = new GameStateService();

    // The scripted starting state: 5 life, 0 GOLD (the spec), 3 cards
    // (tank/bard/mage), no purchased runes.
    this._player = {
      id: playerId,
      life: GameConfig.startingLife,
      gold: 0,
      hand: this._buildStartingHand(),
      battlefield: [],
      ready: false,
      purchasedRunes: [],
    };

    // Re-emit every GameStateService notification as a shell event — the
    // exact pattern AppShell uses, so the real components see the tutorial
    // state changes like server broadcasts.
    this._gameState.onStateChange((state) => {
      this._emit({ type: 'stateChange', data: state });
    });
  }

  // ─── AppShell-compatible surface ─────────────────────────────────────────
  // The components (HandUI, BattlefieldUI, MatchHUD, UpgradeChoiceUI, …)
  // only use this surface of AppShell; main.ts assigns the shell to the
  // module-level `shell` variable (via a cast) so ALL of its render glue
  // keeps working unchanged.

  get playerId(): string {
    return this._playerId;
  }

  get gameState(): AppGamestate {
    return this._gameState.getState();
  }

  get isInMatch(): boolean {
    return this._gameState.isInMatch;
  }

  /**
   * The scripted match is always "connected" (ReconnectUI reads this in
   * show(); nothing can go down in a tutorial — it is local by design).
   */
  get connectionState(): 'connected' {
    return 'connected';
  }

  /** Subscribe to shell events (stateChange). Returns an unsubscribe fn. */
  onEvent(listener: TutorialShellEventListener): () => void {
    this._listeners.push(listener);
    return () => {
      const index = this._listeners.indexOf(listener);
      if (index !== -1) this._listeners.splice(index, 1);
    };
  }

  // ─── Gating ──────────────────────────────────────────────────────────────

  /** Sets which intentions the current tip allows (empty = reject all). */
  setAllowedActions(actions: readonly TutorialAction[]): void {
    this._allowedActions = new Set(actions);
  }

  /** True when the given action is allowed by the current tip. */
  allows(action: TutorialAction): boolean {
    return this._allowedActions.has(action);
  }

  // ─── Scripted state helpers (used by the TutorialService, never by UI) ──

  /** The live player record (read-only view for the service/tests). */
  getPlayer(): {
    gold: number;
    life: number;
    ready: boolean;
    handIds: string[];
    boardIds: string[];
    boardUpgradeCounts: number[];
    purchasedRunes: string[];
  } {
    return {
      gold: this._player.gold,
      life: this._player.life,
      ready: this._player.ready,
      handIds: this._player.hand.map((c) => c.instanceId),
      boardIds: this._player.battlefield.map((m) => m.instanceId),
      boardUpgradeCounts: this._player.battlefield.map((m) => m.upgradeCount),
      purchasedRunes: [...this._player.purchasedRunes],
    };
  }

  /** Raises the player's gold to at least `amount` (a tip's gold grant). */
  topUpGold(amount: number): void {
    if (this._player.gold >= amount) return;
    this._player.gold = amount;
    this._emitStateUpdate();
  }

  /**
   * Plays a hand card to the battlefield BY SCRIPT (not a user action —
   * e.g. the reposition tip guarantees a second minion). No gating.
   */
  scriptedPlayCard(instanceId: string): boolean {
    return this._applyPlay(instanceId, undefined);
  }

  // ─── Intention handlers (the "authoritative" tutorial server) ───────────
  // Signatures mirror the AppShell methods (matchId, …). Each validates the
  // action gating FIRST, then the normal rules, then mutates + broadcasts.

  /** Emits the scripted initial match state (called once at tutorial start). */
  emitInitial(): void {
    if (this._started) return;
    this._started = true;

    const me = this._buildPlayerData();
    this._gameState.processMessage({
      version: PROTOCOL_VERSION,
      messageType: 'MatchStarted',
      matchId: TUTORIAL_MATCH_ID,
      currentRound: 1,
      phase: 'preparation',
      players: [me],
    } as MatchStarted);
    // Frozen preparation timer (42 s — the tutorial never counts down).
    this._gameState.processMessage({
      version: PROTOCOL_VERSION,
      messageType: 'RoundStarted',
      matchId: TUTORIAL_MATCH_ID,
      currentRound: 1,
      phase: 'preparation',
      drawsThisRound: 0,
      preparationTimerSeconds: TUTORIAL_TIMER_SECONDS,
    } as RoundStarted);
    // Ready state: nobody ready yet (1 living player = the tutorial player).
    this._gameState.processMessage({
      version: PROTOCOL_VERSION,
      messageType: 'ReadyStateUpdate',
      matchId: TUTORIAL_MATCH_ID,
      readyPlayerIds: [],
      totalPlayers: 1,
    } as ReadyStateUpdate);
  }

  playMinion(_matchId: string, minionInstanceId: string, targetPosition?: number): boolean {
    if (!this._allows('play')) return false;
    return this._applyPlay(minionInstanceId, targetPosition);
  }

  sellMinion(_matchId: string, minionInstanceId: string): boolean {
    if (!this._allows('sell')) return false;

    const inHandIdx = this._player.hand.findIndex((c) => c.instanceId === minionInstanceId);
    const onBoardIdx = this._player.battlefield.findIndex((m) => m.instanceId === minionInstanceId);
    if (inHandIdx < 0 && onBoardIdx < 0) return false;

    // Sale value = the card's level (same convention as the real server:
    // upgradeCount + 1 gold).
    const card = inHandIdx >= 0
      ? this._player.hand[inHandIdx]
      : this._player.battlefield[onBoardIdx];
    this._player.gold += card.upgradeCount + 1;

    if (inHandIdx >= 0) {
      this._player.hand.splice(inHandIdx, 1);
    } else {
      this._player.battlefield.splice(onBoardIdx, 1);
      this._renumberBoard();
    }
    this._emitStateUpdate();
    return true;
  }

  repositionMinions(_matchId: string, newOrder: string[]): boolean {
    if (!this._allows('reposition')) return false;

    // Must be an exact permutation of the current board (server rule).
    const current = this._player.battlefield;
    if (newOrder.length !== current.length) return false;
    const currentIds = new Set(current.map((m) => m.instanceId));
    const seen = new Set<string>();
    for (const id of newOrder) {
      if (!currentIds.has(id) || seen.has(id)) return false;
      seen.add(id);
    }

    const byId = new Map(current.map((m) => [m.instanceId, m]));
    this._player.battlefield = newOrder.map((id) => byId.get(id)!);
    this._renumberBoard();
    this._emitStateUpdate();
    return true;
  }

  buyCard(_matchId: string): boolean {
    if (!this._allows('buy')) return false;

    const cost = GameConfig.cardPurchaseCost; // no Discount minions in the tutorial
    if (this._player.gold < cost) return false;
    if (this._player.hand.length >= GameConfig.maxHandSize) return false;

    this._player.gold -= cost;
    const def = testMinions.find((m) => m.id === TUTORIAL_BOUGHT_CARD_ID);
    if (!def) return false; // scripted id — cannot be unknown
    this._player.hand.push({
      instanceId: `tut-${def.id}-${++this._instanceSeq}`,
      baseMinionId: def.id,
      currentHp: def.startingHp,
      attack: def.attack,
      minionType: def.minionType,
      upgradeCount: 0,
      abilities: [...def.baseAbilities],
    });
    this._emitStateUpdate();
    return true;
  }

  /**
   * Returns the upgrade options for the selected minion (the real server
   * answers with a pre-selected ability; the tutorial offers the two stat
   * branches — deterministic and enough to teach the upgrade flow).
   */
  getUpgradeOptions(_matchId: string, minionInstanceId: string): UpgradeOptionsResponse {
    const minion = this._player.battlefield.find((m) => m.instanceId === minionInstanceId);
    let options: UpgradeOption[] = [];
    let upgradeCost = GameConfig.upgradeCost;
    if (minion && minion.upgradeCount < GameConfig.maxUpgrades) {
      upgradeCost = GameConfig.upgradeCost + minion.upgradeCount;
      options = TUTORIAL_UPGRADE_BRANCHES.map((b) => ({
        branchId: b.id,
        name: b.name,
        hpChange: b.hpChange,
        attackChange: b.attackChange,
        abilityId: '',
        abilityName: '',
      }));
    }
    const message: UpgradeOptionsResponse = {
      version: PROTOCOL_VERSION,
      messageType: 'UpgradeOptionsResponse',
      matchId: TUTORIAL_MATCH_ID,
      minionInstanceId,
      options,
      upgradeCost,
    };
    this._gameState.processMessage(message);
    return message;
  }

  upgradeMinion(_matchId: string, minionInstanceId: string, branchId: string, _selectedAbilityId?: string): boolean {
    if (!this._allows('upgrade')) return false;

    const minion = this._player.battlefield.find((m) => m.instanceId === minionInstanceId);
    if (!minion || minion.upgradeCount >= GameConfig.maxUpgrades) return false;
    const branch = TUTORIAL_UPGRADE_BRANCHES.find((b) => b.id === branchId);
    if (!branch) return false;

    const cost = GameConfig.upgradeCost + minion.upgradeCount;
    if (this._player.gold < cost) return false;

    this._player.gold -= cost;
    minion.upgradeCount += 1;
    minion.currentHp += branch.hpChange;
    minion.attack += branch.attackChange;
    minion.upgradePathState = `${minion.upgradePathState}_${branch.id}`;
    this._emitStateUpdate();
    return true;
  }

  /**
   * Buys a rune from the in-game rune shop. The tutorial is SCRIPTED: the
   * rune tip teaches the shop by buying the Rune of Power (the first thing
   * the shop lists at 10g, matching the tip's gold grant) — every other
   * rune is rejected, exactly like the real server rejects a rune the
   * player cannot pay for (the tip only ever grants the Power cost).
   */
  buyRune(_matchId: string, runeId: string): boolean {
    if (!this._allows('rune')) return false;

    // Scripted: the tutorial buys the Rune of Power (and nothing else).
    if (runeId !== RUNE_POWER_ID) return false;
    const def = getRuneById(runeId);
    if (!def) return false;
    if (this._player.purchasedRunes.includes(def.id)) return false; // once per game
    if (this._player.gold < def.goldCost) return false;

    this._player.gold -= def.goldCost;
    this._player.purchasedRunes.push(def.id);
    this._emitStateUpdate();
    return true;
  }

  markReady(_matchId: string, ready: boolean): boolean {
    if (!this._allows('ready')) return false;
    if (this._player.ready === ready) return true; // already in that state
    this._player.ready = ready;
    this._gameState.processMessage({
      version: PROTOCOL_VERSION,
      messageType: 'ReadyStateUpdate',
      matchId: TUTORIAL_MATCH_ID,
      readyPlayerIds: ready ? [this._playerId] : [],
      totalPlayers: 1,
    } as ReadyStateUpdate);
    return true;
  }

  /**
   * Resignation is NOT part of the scripted tour (no tip asks for it and
   * ending the tutorial match would break the remaining tips). Rejected as
   * a plain no-op, exactly like every non-allowed intention in the shell —
   * the state stays byte-identical and the tour continues.
   */
  resignMatch(_matchId: string): boolean {
    return false;
  }

  // ─── Internals ───────────────────────────────────────────────────────────

  private _allows(action: TutorialAction): boolean {
    // The intention is only valid inside the (frozen) preparation phase —
    // mirrors the real server's phase check.
    const match = this._gameState.getMatchState();
    if (match.matchId !== TUTORIAL_MATCH_ID || match.phase !== 'preparation') return false;
    return this._allowedActions.has(action);
  }

  private _applyPlay(minionInstanceId: string, targetPosition?: number): boolean {
    const idx = this._player.hand.findIndex((c) => c.instanceId === minionInstanceId);
    if (idx < 0) return false;
    if (this._player.battlefield.length >= GameConfig.maxBoardSize) return false;

    const card = this._player.hand.splice(idx, 1)[0];
    const boardMinion: MinionOnBoardData = {
      ...card,
      battlefieldPosition: 0,
      upgradePathState: 'base',
    };
    // Insert at the requested position (the drop X from the drag), else append.
    const pos = targetPosition !== undefined
      ? Math.max(0, Math.min(targetPosition, this._player.battlefield.length))
      : this._player.battlefield.length;
    this._player.battlefield.splice(pos, 0, boardMinion);
    this._renumberBoard();
    this._emitStateUpdate();
    return true;
  }

  private _renumberBoard(): void {
    this._player.battlefield.forEach((m, i) => {
      m.battlefieldPosition = i;
    });
  }

  private _buildStartingHand(): MinionInHandData[] {
    return TUTORIAL_STARTING_HAND.map((cardId) => {
      const def = testMinions.find((m) => m.id === cardId);
      if (!def) throw new Error(`Tutorial starting card "${cardId}" not found in the card pool`);
      return {
        instanceId: `tut-${def.id}-1`,
        baseMinionId: def.id,
        currentHp: def.startingHp,
        attack: def.attack,
        minionType: def.minionType,
        upgradeCount: 0,
        abilities: [...def.baseAbilities],
      };
    });
  }

  private _buildPlayerData(): PlayerData {
    return {
      id: this._player.id,
      life: this._player.life,
      gold: this._player.gold,
      hand: this._player.hand.map((c) => ({ ...c, abilities: [...c.abilities] })),
      battlefield: this._player.battlefield.map((m) => ({ ...m, abilities: [...m.abilities] })),
      isAlive: true,
      phase: 'preparation',
      currentRound: 1,
      previousOpponentId: null,
      nextOpponentId: null,
      skins: {},
      runes: {
        purchased: [...this._player.purchasedRunes],
      },
    };
  }

  private _emitStateUpdate(): void {
    this._gameState.processMessage({
      version: PROTOCOL_VERSION,
      messageType: 'GameStateUpdate',
      matchId: TUTORIAL_MATCH_ID,
      currentRound: 1,
      phase: 'preparation',
      players: [this._buildPlayerData()],
      gameOver: false,
      winnerId: null,
    } as GameStateUpdate);
  }

  private _emit(event: TutorialShellEvent): void {
    for (const listener of [...this._listeners]) {
      listener(event);
    }
  }
}
